/**
 * Evaluacion del recordatorio de respaldo (Req. 4).
 *
 * En un iPhone los datos locales pueden desaparecer sin aviso, y no hay copia en
 * la nube. Este aviso es la ultima linea de defensa, asi que su logica vive en el
 * dominio y tiene pruebas, en lugar de quedar escondida en un componente.
 *
 * Sin notificaciones del sistema operativo: el aviso se muestra dentro de la
 * aplicacion (Req. 4.5).
 */

export type NivelRespaldo = 'ok' | 'aviso' | 'alerta';

export interface EstadoRespaldo {
  /** Dias completos desde el ultimo respaldo. null si nunca se ha hecho. */
  diasDesdeRespaldo: number | null;
  nivel: NivelRespaldo;
  /** true = mostrar el aviso en el dashboard. */
  debeAvisar: boolean;
  /** Texto listo para mostrar. */
  mensaje: string;
  /** Texto secundario que explica el riesgo. */
  detalle: string | null;
}

export interface ParametrosRespaldo {
  /** Marca ISO del ultimo respaldo exportado con exito. */
  ultimoRespaldoEn: string | null;
  /** Si no hay datos capturados, no hay nada que perder ni que avisar. */
  hayDatos: boolean;
  /** Dias tras los cuales avisar. Predeterminado 7 (Req. 4.2). */
  diasAviso?: number;
  /** Inyectable para poder probar sin depender del reloj. */
  ahora?: Date;
}

/** A partir de este umbral el aviso sube a jerarquia de alerta (Req. 4.3). */
export const DIAS_ALERTA = 14;

const MILISEGUNDOS_POR_DIA = 86_400_000;

export function diasDesde(iso: string, ahora: Date): number | null {
  const marca = Date.parse(iso);
  if (Number.isNaN(marca)) return null;
  const transcurridos = Math.floor((ahora.getTime() - marca) / MILISEGUNDOS_POR_DIA);
  return transcurridos < 0 ? 0 : transcurridos;
}

function plural(dias: number): string {
  return dias === 1 ? '1 dia' : `${dias} dias`;
}

export function evaluarRespaldo(parametros: ParametrosRespaldo): EstadoRespaldo {
  const { ultimoRespaldoEn, hayDatos } = parametros;
  const diasAviso = parametros.diasAviso ?? 7;
  const ahora = parametros.ahora ?? new Date();

  // Sin datos capturados no hay nada que respaldar todavia.
  if (!hayDatos) {
    return {
      diasDesdeRespaldo: ultimoRespaldoEn === null ? null : diasDesde(ultimoRespaldoEn, ahora),
      nivel: 'ok',
      debeAvisar: false,
      mensaje: 'Aun no hay datos que respaldar.',
      detalle: null,
    };
  }

  // Nunca se ha respaldado y ya hay informacion capturada (Req. 4.4).
  if (ultimoRespaldoEn === null) {
    return {
      diasDesdeRespaldo: null,
      nivel: 'alerta',
      debeAvisar: true,
      mensaje: 'Nunca has hecho un respaldo',
      detalle:
        'Tus datos solo existen en este telefono. Si lo pierdes o borras los datos del ' +
        'navegador, no habra forma de recuperarlos.',
    };
  }

  const dias = diasDesde(ultimoRespaldoEn, ahora);

  // Fecha ilegible: se trata como si no hubiera respaldo, que es el lado seguro.
  if (dias === null) {
    return {
      diasDesdeRespaldo: null,
      nivel: 'alerta',
      debeAvisar: true,
      mensaje: 'No se pudo leer la fecha del ultimo respaldo',
      detalle: 'Haz un respaldo nuevo para estar segura.',
    };
  }

  if (dias >= DIAS_ALERTA) {
    return {
      diasDesdeRespaldo: dias,
      nivel: 'alerta',
      debeAvisar: true,
      mensaje: `Ultimo respaldo: hace ${plural(dias)}`,
      detalle: 'Ya paso mucho tiempo. Haz un respaldo ahora para no arriesgar tu informacion.',
    };
  }

  if (dias >= diasAviso) {
    return {
      diasDesdeRespaldo: dias,
      nivel: 'aviso',
      debeAvisar: true,
      mensaje: `Ultimo respaldo: hace ${plural(dias)}`,
      detalle: 'Conviene hacer un respaldo.',
    };
  }

  return {
    diasDesdeRespaldo: dias,
    nivel: 'ok',
    debeAvisar: false,
    mensaje: dias === 0 ? 'Respaldo hecho hoy' : `Ultimo respaldo: hace ${plural(dias)}`,
    detalle: null,
  };
}

/** Texto para la pantalla de respaldos, que muestra la fecha de forma permanente (Req. 4.6). */
export function describirUltimoRespaldo(ultimoRespaldoEn: string | null): string {
  if (ultimoRespaldoEn === null) return 'Nunca';

  const marca = new Date(ultimoRespaldoEn);
  if (Number.isNaN(marca.getTime())) return 'Fecha desconocida';

  const p = (n: number) => String(n).padStart(2, '0');
  return (
    `${p(marca.getDate())}/${p(marca.getMonth() + 1)}/${marca.getFullYear()} ` +
    `a las ${p(marca.getHours())}:${p(marca.getMinutes())}`
  );
}
